import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface SetNameModalProps {
  isOpen: boolean;
  onSave: (setName: string) => void;
  onCancel: () => void;
}

const SetNameModal: React.FC<SetNameModalProps> = ({ isOpen, onSave, onCancel }) => {
  const [setName, setSetName] = useState('');

  // 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleSave = () => {
    if (setName.trim()) {
      onSave(setName.trim());
      setSetName('');
    }
  };

  const handleCancel = () => {
    setSetName('');
    onCancel();
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay onClick={handleCancel} />
      <ModalWrapper>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>추가할 알람 세트 이름</ModalTitle>
        
        <InputContainer>
          <InputLabel>세트 이름을 입력하세요</InputLabel>
          <NameInput
            type="text"
            value={setName}
            onChange={(e) => setSetName(e.target.value)}
            placeholder="예: 평일 알람"
            maxLength={20}
            autoFocus
          />
        </InputContainer>

        <ButtonGroup>
          <CancelButton data-variant="modal-secondary" onClick={handleCancel}>
            취소
          </CancelButton>
          <SaveButton onClick={handleSave} disabled={!setName.trim()}>
            저장
          </SaveButton>
        </ButtonGroup>
      </ModalContent>
      </ModalWrapper>
    </>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  min-height: 100vh;
  padding: 40px 0 160px 0;
  box-sizing: border-box;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 30px;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow);
  border: var(--modal-border);
  margin: auto;
`;

const ModalTitle = styled.h2`
  text-align: center;
  margin-bottom: 30px;
  color: var(--color-text-primary);
  font-size: 1.5rem;
  font-weight: bold;
`;

const InputContainer = styled.div`
  margin-bottom: 30px;
`;

const InputLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: var(--color-text-primary);
  font-weight: 500;
`;

const NameInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: rgba(187, 187, 187, 1);
    box-shadow: 0 0 0 2px rgba(187, 187, 187, 0.6);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const SaveButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled ? 'var(--color-save-button-disabled-bg)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CancelButton = styled.button`
  background: #bbb;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #999;
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

export default SetNameModal; 